// Enforcement gate for mandatory 2FA. Sits inside AppLayout, after the
// isAuthenticated check, and blocks Super Admin / Executive Admin users who
// haven't enabled 2FA yet from reaching the rest of the app — using the
// existing /api/rbac/check-session endpoint's twoFARequired/twoFAEnabled
// fields (already computed server-side from SYSTEM_ROLES.SUPER_ADMIN /
// SYSTEM_ROLES.EXECUTIVE_ADMIN, per Raed's choice of role definition).
//
// This is purely a UX gate, not the security boundary — real enforcement
// still has to live server-side per-endpoint eventually, but check-session
// is already called by the app on every load, so today this is the one
// place that can honestly say "this user is missing required 2FA" and act
// on it immediately, without waiting on a broader backend audit.
import { useEffect, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { TwoFactorSetup } from "./TwoFactorSetup";

interface CheckSessionResult {
  valid: boolean;
  twoFARequired: boolean;
  twoFAEnabled: boolean;
}

export function TwoFactorGate({ children }: { children: React.ReactNode }) {
  const { apiClient, logout } = useAuth();
  const [status, setStatus] = useState<"loading" | "blocked" | "clear">("loading");

  const check = async () => {
    try {
      const result = await apiClient("/api/rbac/check-session", { method: "POST" });
      const data: CheckSessionResult | undefined = result?.data;
      if (data && data.twoFARequired && !data.twoFAEnabled) {
        setStatus("blocked");
      } else {
        setStatus("clear");
      }
    } catch (err) {
      // Fail open here — check-session itself already gates real API access,
      // and a UX gate that breaks the whole app on a transient network blip
      // would be worse than not blocking this one screen.
      console.error("TwoFactorGate: check-session failed", err);
      setStatus("clear");
    }
  };

  useEffect(() => {
    check();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (status === "loading") {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-50">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-600 border-t-transparent"></div>
      </div>
    );
  }

  if (status === "blocked") {
    return <TwoFactorSetup onComplete={check} onSignOut={logout} />;
  }

  return <>{children}</>;
}
